import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Chip, Grid, MenuItem, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { clientAPI, invoiceAPI } from '../services/api';
import { apiError, dateFr, fcfa, INVOICE_STATUS } from '../utils/format';
import { tokens } from '../theme';
import { Kpi, Panel, Toolbar } from '../components/ui';
import { AccountBalanceWallet, CheckCircleOutline, EventBusy, ReceiptLong } from '@mui/icons-material';

const Factures = () => {
  const navigate = useNavigate();
  const { canCompta } = useAuth();
  const [data, setData] = useState({ invoices: [], summary: null });
  const [clients, setClients] = useState([]);
  const [filters, setFilters] = useState({ statut: '', client_id: '' });
  const [error, setError] = useState('');

  useEffect(() => { clientAPI.list(true).then((r) => setClients(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    invoiceAPI.list(params).then((r) => setData(r.data)).catch((err) => setError(apiError(err)));
  }, [filters]);

  const s = data.summary;
  return (
    <Layout title="Factures" plain
      actions={<>
        {canCompta && <Button variant="outlined" onClick={() => navigate('/bons-livraison?statut=VALIDEE')}>Facturer des BL</Button>}
        {canCompta && <Button variant="contained" onClick={() => navigate('/factures/nouvelle')}>Nouvelle facture</Button>}
      </>}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {s && (
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          <Grid item xs={6} md={3}><Kpi icon={ReceiptLong} tint="navy" label="Facturé TTC" value={fcfa(s.total_facture_ttc)} sub="FCFA" valueSize={{ xs: 19, sm: 24 }} /></Grid>
          <Grid item xs={6} md={3}><Kpi icon={CheckCircleOutline} tint="green" label="Encaissé" value={fcfa(s.total_encaisse)} sub="FCFA" valueSize={{ xs: 19, sm: 24 }} /></Grid>
          <Grid item xs={6} md={3}><Kpi icon={AccountBalanceWallet} tint="amber" label="Reste à encaisser" value={fcfa(s.reste_a_encaisser)} sub="FCFA" valueSize={{ xs: 19, sm: 24 }} /></Grid>
          <Grid item xs={6} md={3}><Kpi icon={EventBusy} tint="red" label="En retard" value={fcfa(s.en_retard)} sub={`${s.nb_en_retard} facture(s) · FCFA`} valueSize={{ xs: 19, sm: 24 }}
            valueColor={s.en_retard > 0 ? '#B4232A' : undefined} /></Grid>
        </Grid>
      )}

      <Toolbar>
        <TextField select size="small" label="Statut" value={filters.statut} sx={{ minWidth: 160 }}
          onChange={(e) => setFilters({ ...filters, statut: e.target.value })}>
          <MenuItem value="">Tous</MenuItem>
          {Object.entries(INVOICE_STATUS).map(([k, v]) => <MenuItem key={k} value={k}>{v.label}</MenuItem>)}
        </TextField>
        <TextField select size="small" label="Client" value={filters.client_id} sx={{ minWidth: 200 }}
          onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}>
          <MenuItem value="">Tous</MenuItem>
          {clients.map((c) => <MenuItem key={c.id} value={c.id}>{c.nom}</MenuItem>)}
        </TextField>
      </Toolbar>

      <Panel flush>
      <TableContainer sx={{ overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Numéro</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Échéance</TableCell>
              <TableCell>Client</TableCell>
              <TableCell>BL</TableCell>
              <TableCell align="right">Total TTC</TableCell>
              <TableCell align="right">Réglé</TableCell>
              <TableCell align="right">Net à payer</TableCell>
              <TableCell>Statut</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.invoices.map((i) => (
              <TableRow key={i.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/factures/${i.id}`)}>
                <TableCell sx={{ fontFamily: tokens.mono, fontWeight: 600, whiteSpace: 'nowrap' }}>{i.numero || '—'}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateFr(i.date_facture)}</TableCell>
                <TableCell sx={{ color: i.en_retard ? 'error.main' : undefined }}>{dateFr(i.date_echeance)}</TableCell>
                <TableCell>{i.client_affiche}</TableCell>
                <TableCell sx={{ fontSize: 12, fontFamily: tokens.mono, whiteSpace: 'nowrap' }}>{i.bons_livraison.map((b) => b.bl_numero).join(', ')}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fcfa(i.total_ttc)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>{fcfa(i.total_regle)}</TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{fcfa(i.net_a_payer)}</TableCell>
                <TableCell>
                  <Chip size="small" label={INVOICE_STATUS[i.statut]?.label} color={INVOICE_STATUS[i.statut]?.color} />
                  {i.en_retard && <Chip size="small" label="En retard" color="error" variant="outlined" sx={{ ml: 0.5 }} />}
                  {i.statut === 'EMISE' && i.total_regle > 0 && <Chip size="small" label="Partiel" variant="outlined" sx={{ ml: 0.5 }} />}
                </TableCell>
              </TableRow>
            ))}
            {data.invoices.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ py: 4, color: 'text.secondary' }}>Aucune facture</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer>
      </Panel>
    </Layout>
  );
};

export default Factures;
